import { useMemo } from 'react';

import type { OrderWithCustomProducts } from '../../../data/utils/types';
import { useGetOrder } from '../../../hooks/useGetOrder';
import { OrderDetails, OrderProduct } from '../types';

interface UseOrderDetailsResult {
  order: OrderDetails | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Transform OrderWithCustomProducts to OrderDetails format
 */
const transformOrderToDetails = (
  order: OrderWithCustomProducts | null
): OrderDetails | null => {
  if (!order) return null;

  // Transform products to OrderProduct format
  const products: OrderProduct[] = (order.products || []).map((product) => ({
    id: product.id,
    externalId: product.externalId || null,
    name: product.name,
    unit: product.unit || '',
    amount: product.amount,
  }));

  // Parse createdAt to timestamp
  let createdAt: number;
  if (typeof order.createdAt === 'string') {
    createdAt = new Date(order.createdAt).getTime();
  } else if (typeof order.createdAt === 'number') {
    createdAt = order.createdAt;
  } else {
    createdAt = Date.now();
  }

  return {
    id: order.id,
    referenceId: order.referenceId || order.id,
    status: order.status || 'unknown',
    createdAt,
    deliveryDate: order.deliveryDate,
    body: order.body,
    pdf: order.pdf,
    isTest: order.isTest || false,
    createdContext: order.createdContext as any,
    user: order.user
      ? {
          id: order.user.id,
          name: order.user.name || null,
          phone: order.user.phone || null,
        }
      : null,
    buyer: order.buyer
      ? {
          id: order.id, // Use order id as fallback
          name: order.buyer.name || null,
          fullAddress: order.buyer.fullAddress || null,
        }
      : null,
    supplier: order.supplier
      ? {
          id: order.id, // Use order id as fallback
          name: order.supplier.name || null,
        }
      : null,
    chat: order.chat
      ? {
          id: order.chat.id,
          deliveryAddress: order.chat.deliveryAddress || null,
        }
      : null,
    products,
  };
};

/**
 * Custom hook to fetch order details using GraphQL
 */
export const useOrderDetails = (
  orderId: string | null
): UseOrderDetailsResult => {
  const {
    order: rawOrder,
    loading,
    error,
  } = useGetOrder(
    orderId || '',
    undefined // onError callback
  );

  const order = useMemo(() => {
    if (!rawOrder) return null;
    return transformOrderToDetails(rawOrder);
  }, [rawOrder]);

  return { order, loading, error };
};
